import React, { useCallback, useContext } from 'react';
import { TableContext } from './context/TableContext';
import { EditModeContext } from './context/EditModeContext';
import EditableCell from './components/EditableCell';
import Checkbox from '../Checkbox';
import { ROW_HEIGHT } from './BaseTable';

const TableRow = ({
  item,
  visibleColumns,
  selectedItems,
  onRowClick,
  onCheckboxChange,
  className = '',
  renderCell
}) => {
  const editModeContext = useContext(EditModeContext);
  const isEditMode = editModeContext?.isEditMode || false;
  const { preferences, columnWidths, isColumnPinned } = useContext(TableContext);

  const handleClick = useCallback((e) => {
    // Handle left and middle clicks
    if (e.button === 0 || e.button === 1) {
      // Small delay to allow text selection to register (for left click only)
      if (e.button === 0) {
        setTimeout(() => {
          if (window.getSelection().toString() === '') {
            onRowClick?.(item.id, e.button, e.target);
          }
        }, 0);
      } else {
        // Middle click doesn't need selection check
        onRowClick?.(item.id, e.button, e.target);
      }
    }
  }, [item.id, onRowClick]);

  const handleCheckboxChange = useCallback((e) => {
    onCheckboxChange?.(item.id, e?.shiftKey);
  }, [item.id, onCheckboxChange]);

  // Calculate pinned column offsets
  const getPinnedOffset = useCallback((columnField) => {
    let offset = onCheckboxChange ? 48 : 0; // Width of checkbox column if present
    for (const col of visibleColumns) {
      if (col.field === columnField) break;
      if (isColumnPinned(col.field)) {
        offset += (columnWidths[col.field] || 150);
      }
    }
    return offset;
  }, [visibleColumns, isColumnPinned, columnWidths, onCheckboxChange]);

  return (
    <tr
      data-row-id={item.id}
      style={{ height: `${ROW_HEIGHT[preferences.density] || ROW_HEIGHT.standard}px` }}
      className={`group hover:bg-indigo-50 border-b ${isEditMode ? 'border-gray-300 select-none' : 'border-gray-100'} ${className}`}
      onMouseUp={(e) => {
        // Don't handle clicks from checkbox cell or if already handled
        const target = e.target;
        const isCheckboxCell = target.closest('td[data-checkbox="true"]');
        if (!e._handled && !isCheckboxCell && !isEditMode) {
          handleClick(e);
        }
      }}
    >
      {onCheckboxChange && (
        <td 
          data-checkbox="true"
          className={`px-4 bg-white group-hover:bg-indigo-50 sticky left-0 z-[2] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] 
            ${isEditMode ? 'border-r border-gray-300' : ''} 
            ${selectedItems.has(String(item.id)) ? 'border-t border-b border-indigo-500 before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[1px] before:bg-indigo-500' : ''}`}
          onClick={e => {
            if (!isEditMode) {
              e.preventDefault();
              e.stopPropagation();
              if (e.shiftKey) {
                // Prevent text selection when shift-clicking
                window.getSelection().removeAllRanges();
              }
              handleCheckboxChange(e);
              e._handled = true;
            }
          }}
        >
          <Checkbox
            id={`row-checkbox-${item.id}`}
            name={`row-checkbox-${item.id}`}
            checked={selectedItems.has(String(item.id))}
            onChange={(e) => handleCheckboxChange(e)}
            disabled={isEditMode}
          />
        </td>
      )}
      {visibleColumns.map((column, colIndex) => {
        const isPinned = isColumnPinned(column.field);
        const pinnedOffset = isPinned ? getPinnedOffset(column.field) : null;
        const isLastColumn = colIndex === visibleColumns.length - 1;
        
        return (
          <td 
            key={column.field} 
            data-field={column.field}
            style={{ 
              width: `${columnWidths[column.field] || 150}px`,
              minWidth: `${columnWidths[column.field] || 150}px`,
              maxWidth: `${columnWidths[column.field] || 150}px`,
              left: isPinned ? `${pinnedOffset}px` : 'auto'
            }}
            className={`whitespace-nowrap ${isEditMode ? '' : 'cursor-pointer'} 
              ${preferences.density === 'comfortable' ? 'text-base' : preferences.density === 'standard' ? 'text-sm' : 'text-xs'} 
              text-gray-900 overflow-hidden text-ellipsis max-w-0
              ${isEditMode ? `border-r border-gray-300 ${['image', 'status_name', 'tags', 'purchase_date', 'purchase_price', 'total_cost', 'purchased_from', 'sold_to', 'sold_price'].includes(column.field) ? 'bg-gray-50 text-gray-500' : ''}` : ''}
              ${isPinned 
                ? `sticky z-[2] bg-white group-hover:bg-indigo-50 before:absolute before:inset-0 before:pointer-events-none before:shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] ${selectedItems.has(String(item.id)) ? 'border-t border-b border-indigo-500' : ''}`
                : selectedItems.has(String(item.id)) 
                  ? `border-t border-b border-indigo-500 ${!isPinned && isLastColumn ? 'border-r' : ''}`
                  : ''
              }`}
          >
            {isEditMode && !['image', 'status_name', 'tags', 'purchase_date', 'purchase_price', 'total_cost', 'purchased_from', 'sold_to', 'sold_price'].includes(column.field) ? (
              <EditableCell
                row={item}
                column={column}
                value={typeof item[column.field] === 'object' ?
                  JSON.stringify(item[column.field]) :
                  item[column.field] ?? ''}
                className={preferences.density === 'comfortable' ? 'text-base' : preferences.density === 'standard' ? 'text-sm' : 'text-xs'}
              />
            ) : (
              (() => {
                const content = renderCell ? 
                  renderCell(item, column) : 
                  column.renderCell ? 
                    column.renderCell(item) : 
                    item[column.field];
                
                // Check if content is interactive
                const isInteractive = React.isValidElement(content) && (
                  content.type === 'a' || 
                  content.type === 'button' || 
                  content.props.onClick ||
                  content.type === Checkbox
                );

                return (
                  <div className={`select-text px-4 h-full flex items-center ${isInteractive ? '' : 'pointer-events-none'}`}>
                    {column.type === 'image' || isInteractive ? content : (
                      item.href ? (
                        <a
                          href={item.href}
                          onClick={(e) => {
                            if (e.button === 0 || e.button === 1) {
                              e.preventDefault();
                              if (e.button === 0) {
                                // Small delay to allow text selection to register for left clicks
                                setTimeout(() => {
                                  if (window.getSelection().toString() === '') {
                                    e._handled = true;
                                    handleClick(e);
                                  }
                                }, 0);
                              } else {
                                // Middle click doesn't need selection check
                                e._handled = true;
                                handleClick(e);
                              }
                            }
                          }}
                          className="block h-full"
                        >
                          {content}
                        </a>
                      ) : content
                    )}
                  </div>
                );
              })()
            )}
          </td>
        );
      })}
    </tr>
  );
};

// Don't memoize since we need context updates for density changes
export default TableRow;
